import { toaster } from 'baseui/toast';
import { compact, isNil } from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';
import { FinalButton } from '../../../Components/FinalButton';
import { Icon, IconKey } from '../../../Components/Icons';
import { Link } from '../../../Components/Links';
import { MedicalNotePopover } from '../../../Components/MedicalNote/PopoverNote';
import { usePopoverTabContext } from '../../../Components/PopoverTabs/PopoverTabContainer';
import { hasSelfPayFlow } from '../../../Components/ProviderNetwork/providerNetworkUtils';
import { Text } from '../../../globalStyles';
import {
  BaseRef,
  CareType,
  PaymentSource,
  useGetAllNotesForUserQuery,
  useMarkReferralCompleteMutation,
  useUserReferralStatusQuery,
} from '../../../graphQL';
import { findStatusByCareType } from '../../../utils/careTypeAppointmentStatuses';
import { stringifyParamsBy } from '../../../utils';
import { Restricted, useDrilldownContext } from '../helpers';
import { Styles } from '../styles';
import {
  CareTypeDisplayConfig,
  useCareTypeConfigContext,
} from '../../../Components/CareTypeConfigContext';

export const McpOnboarding = () => {
  const { user, events } = useDrilldownContext();
  const activeCareTypes = user.careTypes;

  // tasks is null when withheld for a Restricted patient; intake state is unknown, so
  // render no scheduling widgets. An empty array means no done tasks, so widgets show.
  const tasks = user.tasks;

  if (!tasks) {
    return null;
  }

  const isInitialConsultationComplete = (careType: CareType) =>
    tasks.some(
      t =>
        t.parsedRef.baseRef === BaseRef.CompleteInitialConsultation &&
        t.parsedRef.careType === careType &&
        t.done
    );

  const isIntakeScheduled = (careType: CareType) =>
    tasks.some(
      t =>
        t.parsedRef.baseRef === BaseRef.ScheduleInitialConsultation &&
        t.parsedRef.careType === careType &&
        t.done
    );

  const shouldSuggestProviders = (careType: CareType) =>
    !!user.organization &&
    hasSelfPayFlow(user.organization, [careType]) &&
    !(user.suggestedProviders ?? []).some(p => (p.careTypes ?? []).includes(careType));

  if (activeCareTypes.every(isInitialConsultationComplete)) {
    return null;
  }

  const completeReferral =
    user.referralComplete ||
    events
      .filter(e => e.tag === 'provider:note:complete')
      .some(n => n.data?.key === 'student-referral');

  const mcpIntakeWidgets = compact(
    activeCareTypes.flatMap(careType => {
      if (isIntakeScheduled(careType)) return [];
      return [
        shouldSuggestProviders(careType) && (
          <SuggestedProvider careType={careType} key={`suggested-${careType}`} />
        ),
        <IncompleteConsultation careType={careType} key={`incomplete-${careType}`} />,
      ];
    })
  );

  const anyIntakeScheduled = activeCareTypes.some(isIntakeScheduled);
  const referralWidgets = compact([
    (completeReferral && anyIntakeScheduled) || (
      <Text.label key="referralKind">University Referral</Text.label>
    ),
    !completeReferral && <IncompleteReferral key="incompleteReferral" />,
  ]);

  const renderRestricted = <Restricted>{referralWidgets}</Restricted>;

  if (
    (!mcpIntakeWidgets.length && !renderRestricted) ||
    (!mcpIntakeWidgets.length && referralWidgets.length)
  ) {
    return null;
  }

  return (
    <Styles.widget>
      <Text.h3>Incomplete</Text.h3>
      <div className="flex flex-column gap-2">
        {mcpIntakeWidgets}
        {renderRestricted}
      </div>
    </Styles.widget>
  );
};

export const ReferralPortalOnboarding = () => {
  const { user } = useDrilldownContext();
  const { data: userReferralStatusQueryData } = useUserReferralStatusQuery({
    variables: {
      patientId: user.id,
    },
  });

  const userReferralStatus = userReferralStatusQueryData?.userReferralStatus;

  if (!userReferralStatus) {
    return null;
  }

  const careTypesNeedingIntake = user.careTypes.filter(ct => {
    const status = findStatusByCareType(userReferralStatus.careTypeAppointmentStatuses, ct);
    return !status?.upcomingAppointment && !status?.completedAppointment;
  });

  if (!careTypesNeedingIntake.length) return null;

  return (
    <Styles.widget>
      {careTypesNeedingIntake.map(ct => (
        <IncompleteConsultation key={ct} careType={ct} />
      ))}
    </Styles.widget>
  );
};

type TaskDisplay = {
  icon: IconKey;
  getTitle: (careType: CareType | null | undefined) => string;
};

const buildTaskMap = (
  getCareTypeDisplay: (careType: string) => CareTypeDisplayConfig
): Partial<Record<BaseRef, TaskDisplay>> => ({
  [BaseRef.ReferredAccountSetup]: {
    icon: 'iconsUserSvg',
    getTitle: () => 'Finish Account Setup',
  },
  [BaseRef.BasicAccountCreation]: {
    icon: 'iconsUserSvg',
    getTitle: () => 'Finish Account Setup',
  },
  [BaseRef.InitialQuestionnaireAssessment]: {
    icon: 'iconsBlackMbcSvg',
    getTitle: () => 'Complete Intake Assessment',
  },
  [BaseRef.MedicalHistory]: {
    icon: 'iconsBlackNoteSvg',
    getTitle: () => 'Fill out Medical History',
  },
  [BaseRef.VerifyIdentity]: {
    icon: 'iconsUserSvg',
    getTitle: () => 'ID Verification',
  },
  [BaseRef.PharmacySelection]: {
    icon: 'iconsBlackRxSvg',
    getTitle: () => 'Select Pharmacy Option',
  },
  [BaseRef.ScheduleInitialConsultation]: {
    icon: 'iconsBlackApptSvg',
    getTitle: careType =>
      careType
        ? `Schedule ${getCareTypeDisplay(careType).appointmentIntakeLabel}`
        : 'Schedule Initial Consultation',
  },
  [BaseRef.CompleteInitialConsultation]: {
    icon: 'iconsBlackVideoSvg',
    getTitle: careType =>
      careType
        ? `Complete ${getCareTypeDisplay(careType).appointmentIntakeLabel}`
        : 'Complete Initial Consultation',
  },
  [BaseRef.TriageFlow]: {
    icon: 'iconsBlackMbcSvg',
    getTitle: () => 'Complete Self-Refer Triage Screening 1',
  },
});

export const OzOnboarding = () => {
  const { user } = useDrilldownContext();
  const { getCareTypeDisplay } = useCareTypeConfigContext();
  const { tasks } = user;

  const taskMap = useMemo(() => buildTaskMap(getCareTypeDisplay), [getCareTypeDisplay]);

  if (!tasks?.length || tasks.every(t => t.done)) {
    return null;
  }

  return (
    <Styles.widget>
      <Text.h3>Incomplete</Text.h3>
      <div className="flex flex-column gap-2">
        <div>
          <Text.label className="mb2">Patient Intake</Text.label>
          {tasks?.map(t => {
            const display = taskMap[t.parsedRef.baseRef];
            if (!display) return null;
            return (
              <Styles.listDiv key={t.id} vPadding="narrow">
                <Icon icon={display.icon} alt={t.title} className="mr2" />
                <div className="ph3">
                  <Text.bodyBold>{display.getTitle(t.parsedRef.careType)}</Text.bodyBold>
                  {t.done ? (
                    <Text.bodyGrey>
                      Completed {moment(t.updatedAt).format('M/D/YYYY hh:mmaz')}
                    </Text.bodyGrey>
                  ) : (
                    <Text.body kind="danger">Incomplete</Text.body>
                  )}
                </div>
              </Styles.listDiv>
            );
          })}
        </div>
      </div>
    </Styles.widget>
  );
};

const IncompleteReferral = () => {
  const { user, refetch } = useDrilldownContext();
  const { pushTab } = usePopoverTabContext();
  const [markNoteComplete] = useMarkReferralCompleteMutation();
  const { data } = useGetAllNotesForUserQuery({
    variables: { id: user.id },
  });

  const referral = data?.adminUser.notes.find(f => f.key === 'student-referral');

  const handleClick = () =>
    pushTab({
      tabKey: 'student-referral',
      title: 'Referral',
      renderBody: props => <MedicalNotePopover noteKey="student-referral" {...props} />,
    });

  const submitMarkAsComplete = async () => {
    try {
      await markNoteComplete({ variables: { userId: user.id } });
      await refetch();
      toaster.positive('Referral marked as complete.', {});
    } catch {
      toaster.negative('Referral could not be marked as complete. Please try again.', {});
    }
  };

  return (
    <Styles.listDiv>
      <Icon icon="iconsBlackNoteSvg" alt="Incomplete Note" className="mr2" />
      <div className="w-100 flex justify-between">
        <div className="ph3">
          <Text.bodyBold>Complete Referral Note</Text.bodyBold>
          <Text.body kind={referral ? 'basic' : 'danger'}>
            {referral ? `Started ${moment(referral.updatedAt).format('M/D/YYYY')}` : 'Not Started'}
          </Text.body>
        </div>
        <div className="flex flex-row items-center">
          <Text.linkButtonSmall
            kind="grayText"
            type="button"
            className="mr4"
            onClick={submitMarkAsComplete}
          >
            Mark as Complete
          </Text.linkButtonSmall>
          <FinalButton className="w4" kind="outline_black" onClick={handleClick}>
            {referral ? 'Continue' : 'Start'}
          </FinalButton>
        </div>
      </div>
    </Styles.listDiv>
  );
};

const IncompleteConsultation = ({ careType }: { careType: CareType }) => {
  const { user } = useDrilldownContext();
  const { getCareTypeDisplay } = useCareTypeConfigContext();
  const { initialConsultationLabel } = getCareTypeDisplay(careType);

  const isSelfPay =
    user.organization?.careFlows.find(i => i.careType === careType)?.defaultPaymentSource ===
    PaymentSource.SelfPay;

  const scheduleVia = isSelfPay
    ? `/provider-network/book?userId=${user.id}&careType=${careType}`
    : `/book?userId=${user.id}&referral=1&careType=${careType}`;

  return (
    <Styles.listDiv>
      <Icon icon="iconsBlackApptSvg" alt="Appointment" className="mr2" />
      <div className="w-100 flex justify-between">
        <div className="ph3">
          <Text.bodyBold>{`Schedule ${initialConsultationLabel}`}</Text.bodyBold>
          <Text.bodyGrey>
            Optional for staff. Patient can book appointments from their Mantra Patient Portal.
          </Text.bodyGrey>
        </div>
        <Link to={scheduleVia} className="link">
          <FinalButton kind="outline_black" className="w4">
            Schedule
          </FinalButton>
        </Link>
      </div>
    </Styles.listDiv>
  );
};

const SuggestedProvider = ({ careType }: { careType: CareType }) => {
  const { getCareTypeDisplay } = useCareTypeConfigContext();
  const { providerTitle } = getCareTypeDisplay(careType);
  const { user } = useDrilldownContext();
  const state = user.primaryAddressState;
  const params = stringifyParamsBy({ state, careType, userId: user.id }, v => !isNil(v));
  return (
    <Styles.listDiv>
      <Icon icon="iconsBlackStarSvg" size={24} alt="Suggested Providers" className="mr2" />
      <div className="w-100 flex justify-between">
        <div className="ph3">
          <Text.bodyBold>Select Suggested {providerTitle}(s)</Text.bodyBold>
          <Text.bodyGrey>
            Optional. Suggested Providers will be featured during patient sign-up.
          </Text.bodyGrey>
        </div>
        <Link className="link" to={`/provider-network/suggested-providers?${params}`}>
          <FinalButton className="w4" kind="outline_black">
            Select
          </FinalButton>
        </Link>
      </div>
    </Styles.listDiv>
  );
};
